/**
 * Thin, typed fetch wrapper shared by every Wheez app.
 * Platform-agnostic: works under web (fetch/EventSource) and React Native
 * (fetch via Expo's polyfill; SSE via `sseStream` below since RN has no
 * native EventSource).
 */

export type TokenPair = { accessToken: string; refreshToken?: string; expiresAt?: number };

export interface TokenStore {
  get(): Promise<TokenPair | null>;
  set(tokens: TokenPair | null): Promise<void>;
}

/** In-memory token store — fine for short-lived web sessions; apps should
 * pass a persistent store (SecureStore on RN, localStorage on web). */
export class MemoryTokenStore implements TokenStore {
  private tokens: TokenPair | null = null;
  async get() { return this.tokens; }
  async set(t: TokenPair | null) { this.tokens = t; }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ClientConfig {
  baseUrl: string;
  tokenStore: TokenStore;
  /** Called with (path, body) to hit a refresh endpoint and return new tokens, or null if refresh isn't possible. */
  refresh?: (refreshToken: string) => Promise<TokenPair | null>;
  /** Called when the client gives up (401 after refresh attempt, or no refresh fn) — apps use this to route to login. */
  onUnauthenticated?: () => void;
}

type Json = Record<string, unknown> | unknown[];

export class ApiClient {
  constructor(private cfg: ClientConfig) {}

  private async authHeader(): Promise<Record<string, string>> {
    const t = await this.cfg.tokenStore.get();
    return t?.accessToken ? { Authorization: `Bearer ${t.accessToken}` } : {};
  }

  private async tryRefreshTokens(): Promise<boolean> {
    if (!this.cfg.refresh) return false;
    const t = await this.cfg.tokenStore.get();
    if (!t?.refreshToken) return false;
    const next = await this.cfg.refresh(t.refreshToken).catch(() => null);
    if (!next?.accessToken) {
      await this.cfg.tokenStore.set(null);
      this.cfg.onUnauthenticated?.();
      return false;
    }
    await this.cfg.tokenStore.set(next);
    return true;
  }

  async request<T = unknown>(
    method: string,
    path: string,
    opts: { body?: Json; query?: Record<string, string | number | boolean | undefined>; idempotencyKey?: string; ifMatch?: string; retry?: boolean } = {},
  ): Promise<T> {
    const url = new URL(this.cfg.baseUrl.replace(/\/$/, '') + path);
    if (opts.query) {
      for (const [k, v] of Object.entries(opts.query)) {
        if (v !== undefined) url.searchParams.set(k, String(v));
      }
    }
    const headers: Record<string, string> = {
      ...(await this.authHeader()),
      ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
      ...(opts.idempotencyKey ? { 'Idempotency-Key': opts.idempotencyKey } : {}),
      ...(opts.ifMatch ? { 'If-Match': opts.ifMatch } : {}),
    };
    const res = await fetch(url.toString(), {
      method,
      headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    });

    if (res.status === 401 && !opts.retry && (await this.tryRefreshTokens())) {
      return this.request<T>(method, path, { ...opts, retry: true });
    }

    if (res.status === 204) return undefined as T;

    const text = await res.text();
    const data = text ? JSON.parse(text) : undefined;

    if (!res.ok) {
      const code = (data as any)?.error?.code ?? (data as any)?.code ?? 'UNKNOWN';
      const message = (data as any)?.error?.message ?? (data as any)?.message ?? res.statusText;
      if (res.status === 401 && !this.cfg.refresh) this.cfg.onUnauthenticated?.();
      throw new ApiError(res.status, code, message, (data as any)?.error?.details ?? (data as any)?.details);
    }
    return data as T;
  }

  get<T = unknown>(path: string, query?: Record<string, string | number | boolean | undefined>) {
    return this.request<T>('GET', path, { query });
  }
  post<T = unknown>(path: string, body?: Json, idempotencyKey?: string) {
    return this.request<T>('POST', path, { body, idempotencyKey });
  }
  patch<T = unknown>(path: string, body?: Json) {
    return this.request<T>('PATCH', path, { body });
  }
  put<T = unknown>(path: string, body?: Json) {
    return this.request<T>('PUT', path, { body });
  }
  delete<T = unknown>(path: string) {
    return this.request<T>('DELETE', path);
  }

  /**
   * Streams Server-Sent Events from `path` using a chunked fetch reader —
   * works identically on web and React Native (no native EventSource
   * dependency). Returns an unsubscribe function.
   */
  sseStream(path: string, onEvent: (event: string, data: unknown) => void, onError?: (e: unknown) => void): () => void {
    const controller = new AbortController();
    (async () => {
      try {
        const openStream = async () => {
          const headers = await this.authHeader();
          return fetch(this.cfg.baseUrl.replace(/\/$/, '') + path, {
            headers: { ...headers, Accept: 'text/event-stream' },
            signal: controller.signal,
          });
        };
        let res = await openStream();
        if (res.status === 401 && (await this.tryRefreshTokens())) res = await openStream();
        if (!res.ok || !res.body) throw new Error(`SSE ${path} failed: ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let idx: number;
          while ((idx = buf.indexOf('\n\n')) !== -1) {
            const chunk = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            let event = 'message';
            const dataLines: string[] = [];
            for (const line of chunk.split('\n')) {
              if (line.startsWith('event:')) event = line.slice(6).trim();
              else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
            }
            if (dataLines.length) {
              const raw = dataLines.join('\n');
              try {
                onEvent(event, JSON.parse(raw));
              } catch {
                onEvent(event, raw);
              }
            }
          }
        }
      } catch (e) {
        if ((e as any)?.name !== 'AbortError') onError?.(e);
      }
    })();
    return () => controller.abort();
  }
}
